import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { HiOutlineCalendar } from "react-icons/hi";
import { HiOutlineExclamationTriangle } from "react-icons/hi2";
import { Link } from "react-router-dom";
import Badge from "./Badge";

import { api } from "../services/api";
import {
  convertTimeStringFromUTC,
  getTimeUntil,
  isUpcoming,
} from "../utils/dateUtils";
import { getStatusVariant } from "../utils/styleUtils";
import Button from "./Button";
import Card from "./Card";
import LoadingSpinner from "./LoadingSpinner";
import PageHeader from "./PageHeader";
import Select from "./Select";

/* -------------------------------------------------------------------------- */
/* Utilities                                                                  */
/* -------------------------------------------------------------------------- */

/* -------------------------------------------------------------------------- */
/* Utilities                                                                  */
/* -------------------------------------------------------------------------- */

const formatPreferredTime = (preferredTimes = []) => {
  if (!preferredTimes.length) return "Not provided";

  const [{ date, start, end }, ...rest] = preferredTimes;

  const dateStr = date
    ? dayjs.utc(date).local().format("MMM D, YYYY")
    : "Flexible";

  const timeStr = [
    convertTimeStringFromUTC(start),
    convertTimeStringFromUTC(end),
  ]
    .filter(Boolean)
    .join(" - ");

  return `${dateStr}${timeStr ? ` ${timeStr}` : ""}${rest.length ? ` (+${rest.length} more)` : ""
    }`;
};

/* -------------------------------------------------------------------------- */
/* UI Components                                                              */
/* -------------------------------------------------------------------------- */

const EmptyState = () => (
  <Card className="text-center">
    <HiOutlineCalendar className="mx-auto h-12 w-12 text-text/40 mb-3" />
    <p className="text-text opacity-60 mb-2">No bookings or sessions yet</p>
    <p className="text-sm text-text/60">
      New requests and sessions will appear here.
    </p>
  </Card>
);

const CombinedCard = ({ item, isTutor }) => {
  const isPendingBooking =
    item.type === "booking" &&
    ["pending", "awaiting_tutor_confirmations"].includes(item.status);

  return (
    <Card className={isPendingBooking ? "border-2 border-warning" : ""}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h3 className="font-semibold text-lg">{item.subject?.name}</h3>

          {item.subject?.code && (
            <p className="text-sm text-text/70">{item.subject.code}</p>
          )}

          {item.primaryTimeLabel && (
            <p className="text-sm text-text/70 mt-1">{item.primaryTimeLabel}</p>
          )}

          {item.person && (
            <p className="text-sm text-text/70 mt-1">
              {item.personLabel}: {item.person?.name?.first}{" "}
              {item.person?.name?.last}
            </p>
          )}

          {item.secondaryTimeLabel && (
            <p className="text-xs text-text/60 mt-1">
              {item.secondaryTimeLabel}
            </p>
          )}

          <p className="text-xs text-text/60 mt-1">
            {item.type === "booking" ? "Booking Request" : "Session"}
          </p>

          {isPendingBooking && isTutor && (
            <p className="text-xs text-warning mt-2 font-semibold flex items-center gap-1">
              <HiOutlineExclamationTriangle /> Action Required
            </p>
          )}
        </div>

        <Badge
          label={item.status.replace(/_/g, " ")}
          variant={getStatusVariant(item.status)}
          size="small"
          className="font-medium capitalize min-w-[6rem]"
        />
      </div>

      <Link to={item.link} className="block mt-4">
        <Button
          className="w-full"
          variant={isPendingBooking && isTutor ? "secondary" : "default"}
        >
          {isPendingBooking && isTutor ? "Respond to Booking" : "View Details"}
        </Button>
      </Link>
    </Card>
  );
};

/* -------------------------------------------------------------------------- */
/* Main Component                                                             */
/* -------------------------------------------------------------------------- */

export default function SessionsList({
  userRole = "student",
  userId = null,
  showHeader = true,
  title = "Bookings & Sessions",
  subtitle = "All your requests and scheduled sessions",
}) {
  const [bookings, setBookings] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");

  const isStudent = userRole === "student";
  const isTutor = userRole === "tutor";

  const loadBookings = useCallback(async () => {
    try {
      const { bookings = [] } = await api.get("/bookings/user/my-bookings", {
        withCredentials: true,
      });
      setBookings(bookings);
    } catch {
      setBookings([]);
    }
  }, []);

  const loadSessions = useCallback(async () => {
    try {
      const { sessions = [] } = await api.get("/sessions", {
        withCredentials: true,
      });
      setSessions(sessions);
    } catch {
      setSessions([]);
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadBookings(), loadSessions()]);
      setLoading(false);
    })();
  }, [loadBookings, loadSessions]);

  const combinedItems = useMemo(() => {
    const bookingItems = bookings.map((b) => ({
      id: b._id,
      type: "booking",
      subject: b.subjectId,
      status: b.status,
      person: isStudent ? b.assignedTutorId : b.studentId,
      personLabel: isStudent ? "Tutor" : "Student",
      link:
        isTutor && b.status === "awaiting_tutor_confirmations" && userId
          ? `/bookings/${b._id}/tutors/${userId}/respond`
          : `/bookings/${b._id}`,
      primaryTimeLabel: formatPreferredTime(b.preferredTimes),
      secondaryTimeLabel: `Requested ${dayjs(b.createdAt).fromNow()}`,
      sortTime: dayjs(b.createdAt).valueOf(),
    }));

    const sessionItems = sessions.map((s) => ({
      id: s._id,
      type: "session",
      subject: s.subjectId,
      status: s.status || "scheduled",
      person: isStudent ? s.tutorId : s.studentId,
      personLabel: isStudent ? "Tutor" : "Student",
      link: `/sessions/${s._id}`,
      primaryTimeLabel: s.startAt
        ? dayjs.utc(s.startAt).local().format("MMM D, YYYY [at] h:mm A")
        : "TBD",
      secondaryTimeLabel: s.startAt
        ? isUpcoming(s.startAt)
          ? `Starts ${getTimeUntil(s.startAt)}`
          : `Ended ${dayjs.utc(s.startAt).local().fromNow()}`
        : "",
      sortTime: dayjs.utc(s.startAt || 0).valueOf(),
    }));

    return [...bookingItems, ...sessionItems]
      .filter((i) => !statusFilter || i.status === statusFilter)
      .sort((a, b) => b.sortTime - a.sortTime);
  }, [bookings, sessions, statusFilter, isStudent, isTutor, userId]);

  if (loading) {
    return <LoadingSpinner size="lg" className="py-16" />;
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {showHeader && <PageHeader title={title} subtitle={subtitle} />}

      <Select
        label="Filter by status"
        value={statusFilter}
        onChange={(e) => setStatusFilter(e.target.value)}
        options={[
          { value: "", label: "All statuses" },
          { value: "pending", label: "Pending" },
          { value: "awaiting_tutor_confirmations", label: "Awaiting tutor" },
          { value: "confirmed", label: "Confirmed" },
          { value: "scheduled", label: "Scheduled" },
          { value: "ongoing", label: "Ongoing" },
          { value: "completed", label: "Completed" },
          { value: "cancelled", label: "Cancelled" },
          { value: "failed", label: "Failed" },
          { value: "no_show", label: "No show" },
        ]}
      />

      {combinedItems.length ? (
        <div className="space-y-4 mt-4">
          {combinedItems.map((item) => (
            <CombinedCard
              key={`${item.type}-${item.id}`}
              item={item}
              isTutor={isTutor}
            />
          ))}
        </div>
      ) : (
        <EmptyState />
      )}
    </div>
  );
}
